import * as React from 'react';
import { useCallback } from 'react';
import { CommonInputProps, InputHelperText, InputPropTypes, sanitizeInputRestProps } from 'react-admin';

import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup, { FormGroupProps } from '@mui/material/FormGroup';
import FormHelperText from '@mui/material/FormHelperText';
import Switch, { SwitchProps } from '@mui/material/Switch';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { FieldTitle, useInput } from 'ra-core';

/**
 * This is a patch of the React-Admin 'BooleanInput' component.
 * It is needed to prevent the entire row that the BooleanInput is in
 * from being clickable.
 * With this component, only the underlying Switch is clickable (so not even the Label is).
 * BooleanInput documentation can be found here: https://marmelab.com/react-admin/BooleanInput.html
 * This component was createed on Jun 19, 2023, so when you're
 * looking at this code, BooleanInput might have changed since then.
 * As a result, this component might have to be updated accordingly.
 * Sorry about this!
 * ~ Sunny Junejo. I worked on Ausbiz code once
 */
export const LabelNotClickableBoolInput = (props: LabelNotClickableBoolInputProps) => {
  const {
    className,
    row = false,
    defaultValue = false,
    format,
    label,
    fullWidth,
    helperText,
    onBlur,
    onChange,
    onFocus,
    disabled,
    parse,
    resource,
    source,
    validate,
    options,
    sx,
    ...rest
  } = props;
  const {
    id,
    field,
    isRequired,
    fieldState: { error, invalid, isTouched },
    formState: { isSubmitted },
  } = useInput({
    defaultValue,
    format,
    parse,
    resource,
    source,
    onBlur,
    onChange,
    type: 'checkbox',
    validate,
    ...rest,
  });

  const handleChange = useCallback(
    (event: any) => {
      field.onChange(event);
      // Ensure field is considered as touched
      field.onBlur();
    },
    [field]
  );

  return (
    <FormGroup className={clsx('ra-input', `ra-input-${source}`, className)} row={row} sx={sx}>
      <FormControlLabel
        style={{ pointerEvents: 'none' }}
        control={
          <Switch
            id={id}
            name={field.name}
            color="primary"
            onChange={handleChange}
            onFocus={onFocus}
            checked={field.value}
            {...sanitizeInputRestProps(rest)}
            {...options}
            style={{ pointerEvents: 'auto' }}
            disabled={disabled}
          />
        }
        label={<FieldTitle label={label} source={source} resource={resource} isRequired={isRequired} />}
      />
      <FormHelperText error={(isTouched || isSubmitted) && invalid}>
        <InputHelperText touched={isTouched || isSubmitted} error={error?.message} helperText={helperText} />
      </FormHelperText>
    </FormGroup>
  );
};

LabelNotClickableBoolInput.propTypes = {
  ...InputPropTypes,
  // @ts-ignore
  options: PropTypes.shape(Switch.propTypes),
  disabled: PropTypes.bool,
};

LabelNotClickableBoolInput.defaultProps = {
  options: {},
};

export type LabelNotClickableBoolInputProps = CommonInputProps &
  SwitchProps &
  Omit<FormGroupProps, 'defaultValue' | 'onChange' | 'onBlur' | 'onFocus'> & {
    options: SwitchProps;
  };
